import {
  BuyerOfferAnalysisFragment,
  CompetitivenessAdjustment,
  KeyTermUnit,
  OfferScoreResultFragment,
} from '@client/graphql/__generated__/types';
import { CopilotActions } from '@common/enums';
import { useCopilotAction } from '@copilotkit/react-core';
import { Role } from '@copilotkit/runtime-client-gql';
import { useAgent } from '@copilotkitnext/react';
import { lowerCase, startCase } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import {
  AgentContingencyMap,
  AgentKeyTermMap,
  mapAgentOfferRecs,
} from '~/apps/consumer/components/BuyerOfferAnalysis/Context/utils';
import { objectToCsv } from '~/common/utils/formatter';
import { classify } from '~/services/document/utils/string';
import { useCopilotReadableContext } from '../hooks/useCopilotReadableContext';
import { useSaveRestoreMessages } from '../hooks/useSaveRestoreMessages';

export const useOfferRecsActions = (
  buyerOfferAnalysis?: BuyerOfferAnalysisFragment,
  setKeyTerms?: (value: React.SetStateAction<AgentKeyTermMap>) => void
) => {
  const { agent } = useAgent();

  const originalAgentOfferRecs = useMemo(
    () => mapAgentOfferRecs(buyerOfferAnalysis),
    [buyerOfferAnalysis]
  );

  const formatValue = useCallback(
    (key: string, value: number) => {
      // Round to nearest whole number
      const roundedValue = Math.round(value);

      const indigoRecUnit = buyerOfferAnalysis?.indigoOfferRecs?.keyTerms?.find(
        (k) => k.type === key
      )?.unit;

      return indigoRecUnit === KeyTermUnit.USD
        ? new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
          }).format(roundedValue)
        : indigoRecUnit === KeyTermUnit.DAYS
          ? `${roundedValue} day${roundedValue === 1 ? '' : 's'}`
          : roundedValue;
    },
    [buyerOfferAnalysis]
  );

  const onKeyTermsUpdate = useCallback(
    (agentOfferRecs: {
      keyTerms: AgentKeyTermMap;
      contingencies: AgentContingencyMap;
    }) => {
      const keyTermsDiffs = Object.keys(agentOfferRecs.keyTerms)
        ?.filter(
          (key) => typeof agentOfferRecs.keyTerms[key]?.value === 'number'
        )
        ?.filter(
          (key) =>
            agentOfferRecs.keyTerms[key]?.value !==
            originalAgentOfferRecs?.keyTerms?.[key]?.value
        )
        .map((key) => {
          const prevValue = originalAgentOfferRecs?.keyTerms?.[key]?.value;
          const newValue = agentOfferRecs.keyTerms[key].value;
          const termName = classify(key).replace(/_/g, ' ');

          // prevValue could be undefined or null
          return prevValue != null
            ? `updated ${termName} from ${formatValue(
                key,
                prevValue || 0
              )} to ${formatValue(key, newValue || 0)}`
            : `entered ${termName} initial value of ${formatValue(
                key,
                newValue || 0
              )}`;
        });

      const contingenciesDiffs = Object.keys(agentOfferRecs.contingencies)
        ?.filter(
          (key) => typeof agentOfferRecs.contingencies[key]?.value === 'boolean'
        )
        ?.filter(
          (key) =>
            agentOfferRecs.contingencies[key]?.value !==
            originalAgentOfferRecs?.contingencies?.[key]?.value
        )
        .map((key) => {
          const newValue = agentOfferRecs.contingencies[key]?.value;
          const termName = classify(key).replace(/_/g, ' ');

          // prevValue could be undefined or null
          if (originalAgentOfferRecs?.contingencies?.[key]?.value == null) {
            return `${
              newValue ? 'included' : 'excluded'
            } ${termName} contingency`;
          }

          const action = newValue ? 'added' : 'removed';

          return `${action} ${termName} contingency`;
        });

      if (keyTermsDiffs.length > 0 || contingenciesDiffs.length > 0) {
        agent?.addMessage({
          id: `run-${window.crypto.randomUUID()}`,
          role: Role.User,
          content: `You ${keyTermsDiffs
            ?.concat(contingenciesDiffs)
            .join(', and you ')}`,
        });
      }
    },
    [
      formatValue,
      originalAgentOfferRecs?.contingencies,
      originalAgentOfferRecs?.keyTerms,
      agent,
    ]
  );

  const onCompetitivenessUpdate = useCallback(
    (competitivenessAdjustment: CompetitivenessAdjustment) => {
      const previousCompetitiveness =
        originalAgentOfferRecs?.competitivenessAdjustment;

      if (
        previousCompetitiveness?.adjustedLevel ===
          competitivenessAdjustment.adjustedLevel &&
        previousCompetitiveness?.reason === competitivenessAdjustment.reason
      ) {
        return;
      }
      const level = startCase(
        lowerCase(competitivenessAdjustment.adjustedLevel)
      );
      const reason = competitivenessAdjustment.reason;

      agent?.addMessage({
        id: `run-${window.crypto.randomUUID()}`,
        role: Role.User,
        content: `You updated the competitiveness of the offer to "${level}" ${
          reason ? `because ${reason}` : ''
        }`,
      });
    },
    [agent, originalAgentOfferRecs?.competitivenessAdjustment]
  );

  const onScoreUpdate = useCallback(
    (offerScoreResult?: OfferScoreResultFragment) => {
      if (!offerScoreResult) {
        return;
      }

      const isOfferScore = offerScoreResult.__typename === 'OfferScore';
      agent?.addMessage({
        id: `run-${window.crypto.randomUUID()}`,
        role: Role.Assistant,
        content: isOfferScore
          ? offerScoreResult.offerScoreExplanation ||
            `Your new offer score is ${offerScoreResult.value}.`
          : offerScoreResult.reason,
      });
    },
    [agent]
  );

  useCopilotReadableContext({
    description: 'Offer Coach heuristics',
    value: `
## Financial terms (affordability, loan type, down payment amt, available cash)

- FHA, VA offers tend to require better offer terms (purchase price and due diligence fees; shorter DDP) than the equivalent Conventional (and certainly Cash) offers
- Conventional down payment amount affects offer strength
    - 20% is the strongest, with 5% less desirable (even when compared to FHA & VA offers)
- Discount to purchase price if high DDF is offered
- Full desktop underwriting pre-approval (vs. basic pre-qualification letter) can show the buyer is strong and very interested
- Closing costs
    - Buyer & seller pay customary closing costs state by state
    - Buyer can offer to cover seller's costs (attorney fee, recording fee, moving costs, commission, etc…) to strengthen offer

## Contingencies

- If an offer is written with a home sale contingency (typically longer DD period in NC), purchase price and/or DDF should be increased to make the offer more attractive
- Inspection and appraisal waivers make an offer stronger
    - Appraisal waivers are typically conventional buyers purchasing SFR & condo/TH with a high credit score, specific DTI and large down payment amt (20%+); used by specific lenders
        - Not available for FHA, VA or USDA
    - Buyer can complete an inspection, but will agree to make all repairs (and not ask seller)
    - FHA & VA buyers can agree to complete / pay for repairs required on the appraisal (ease seller's mind) if they have the funds to do so
    - VA buyers can agree to reimburse the seller for the WDIR (required to be paid for by the seller per VA)
- Possession agreement --> use to strengthen offer terms
    - Offer free of charge if seller has requested to remain in the home after closing
    - If the home is occupied, buyer to offer seller to remain free of charge for a specific period of time to help ease the burden of moving
    - If the home is vacant, buyer to offer seller to move in before closing for a fee

## Time

- How long has the home been listed for sale (DOM)?
    - Price reductions?
- Is the seller purchasing another home & is their purchase contingent upon their current home closing?
- Is the home occupied or vacant?
- Current inventory & comps (time to sell)
- Market conditions: seller, balanced, buyer

## Key Terms Explanation

- Purchase price: The amount of money the buyer is offering to pay for the home.
- Due diligence: Non-refundable deposit paid by the buyer
- Earnest money deposit: Refundable deposit paid by the buyer
- Offer to close days: Days between the offer date and the closing date (shorter is better)
- Due diligence days: Days between the offer date and the due diligence date (shorter is better)
- Seller concessions: Money paid BY THE SELLER to the BUYER for any BUYER incurred closing costs. (Lower is better for the seller)
  - Make sure you explain seller concessions as the buyer wanting the seller to pay buyer's portion of the closing costs
  - Use the term "requesting seller concessions" not "offering seller concessions" when explaining seller concessions`,
  });

  useCopilotReadableContext({
    description: 'Offer key terms recommendations data',
    value: buyerOfferAnalysis?.indigoOfferRecs?.keyTerms,
  });

  useCopilotReadableContext({
    description: 'Offer contingencies recommendations data',
    value: buyerOfferAnalysis?.indigoOfferRecs?.contingencies,
  });

  useCopilotReadableContext({
    description:
      "Offer competitiveness market data, not the buyer's offer. Meaning if the area around the listing is competitive or not.",
    value: buyerOfferAnalysis?.indigoOfferRecs?.competitiveness,
  });

  useCopilotReadableContext({
    description: 'Listing to buy data',
    value: buyerOfferAnalysis?.listing?.mlsListing,
  });

  useCopilotReadableContext({
    description: 'Buyer Agent offer key terms',
    value: buyerOfferAnalysis?.agentOfferRecs?.keyTerms,
  });

  useCopilotReadableContext({
    description: 'Buyer Agent offer score',
    value:
      buyerOfferAnalysis?.offerScore?.__typename === 'OfferScore'
        ? buyerOfferAnalysis.offerScore.value
        : undefined,
  });

  useCopilotAction({
    name: CopilotActions.updateOfferRecsKeyTerms,
    description: [
      'Only when user explicitly asks to update the offer, modify and complete the key terms of an offer using detailed recommendations data.',
      'Be specific in the updates required and ensure the revisions align with the recommended guidelines for maximum accuracy.',
    ].join(' '),
    parameters: [
      {
        name: 'keyTerms',
        type: 'object[]',
        description: 'Array of key terms to update',
        required: true,
        attributes: [
          {
            name: 'keyTermType',
            type: 'string',
            description: 'Type of key term to update',
            required: true,
          },
          {
            name: 'keyTermUnit',
            type: 'string',
            description: 'Unit of the key term',
            required: false,
          },
          {
            name: 'keyTermValue',
            type: 'string',
            description: 'Value of the key term',
            required: true,
          },
        ],
      },
      {
        name: 'shouldUpdate',
        description: 'Whether to update the key terms of the offer',
        type: 'boolean',
        required: true,
      },
    ],
    handler: (params) => {
      if (!params.shouldUpdate) {
        return objectToCsv(params.keyTerms);
      }

      setKeyTerms?.((prev) => {
        const updated = { ...prev };
        params.keyTerms.forEach((keyTerm) => {
          const keyTermType = keyTerm.keyTermType;
          updated[keyTermType] = {
            ...prev[keyTermType],
            value: Number(keyTerm.keyTermValue),
          };
        });

        return updated;
      });

      return `Provide short summary of the updated key terms in a list format. ${objectToCsv(
        params.keyTerms
      )}`;
    },
  });

  // Save/Restore Chat Messages
  useSaveRestoreMessages(
    buyerOfferAnalysis?.id ? `boa-${buyerOfferAnalysis?.id}` : undefined
  );

  return {
    onKeyTermsUpdate,
    onScoreUpdate,
    onCompetitivenessUpdate,
  };
};
